import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { heroReveal } from "@/lib/motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { HeroBookingWidget } from "@/components/HeroBookingWidget";
import { useLanguage } from "@/context/LanguageContext";
import { useSeo } from "@/lib/useSeo";
import { PAGE_SEO } from "@/data/pageSeo";
import { IMAGES } from "@/data/images";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Presentation, GraduationCap, Trophy, MapPinned, Hotel, Users, Check, ArrowRight, Phone } from "lucide-react";

const audiences = [
  { icon: Presentation, key: "a1" },
  { icon: GraduationCap, key: "a2" },
  { icon: Trophy, key: "a3" },
  { icon: MapPinned, key: "a4" },
  { icon: Hotel, key: "a5" },
  { icon: Users, key: "a6" },
];

export default function Groups() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  useSeo({ ...(PAGE_SEO.groups[lang] || PAGE_SEO.groups.en), path: PAGE_SEO.groups.path, image: PAGE_SEO.groups.image, lang });
  const phone = t("hero.phone");

  return (
    <div className="bg-background text-foreground overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.charterBus} alt="Group transportation charter bus in Montréal" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/72 to-black/55" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-16 w-full grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs tracking-luxe uppercase text-white/60 mb-5">{t("grp.eyebrow")}</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-white">{t("grp.title")}</h1>
            <p className="text-white/70 text-base sm:text-lg max-w-xl mt-6 leading-relaxed">{t("grp.subtitle")}</p>
            <a href={`tel:+1${phone.replace(/[^0-9]/g, "")}`} className="inline-block mt-8">
              <Button variant="outline" className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 h-12 px-7">
                <Phone className="h-4 w-4 mr-2" /> {t("hero.call")} · {phone}
              </Button>
            </a>
          </div>
          <motion.div {...heroReveal}>
            <HeroBookingWidget />
          </motion.div>
        </div>
      </section>

      {/* Who we serve */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <h2 className="font-serif font-light text-4xl sm:text-5xl text-white mb-14 max-w-2xl leading-tight">{t("grp.who.title")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {audiences.map((a) => (
            <div key={a.key} className="rounded-xl border border-white/10 bg-card p-7" data-testid={`grp-aud-${a.key}`}>
              <div className="h-11 w-11 rounded-full border border-white/20 flex items-center justify-center mb-5">
                <a.icon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-white text-lg font-medium mb-2">{t(`grp.${a.key}.t`)}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{t(`grp.${a.key}.d`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Capabilities */}
      <section className="border-y border-white/10 bg-card/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-white mb-8 leading-tight">{t("grp.cap.title")}</h2>
            <ul className="space-y-4">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <li key={n} className="flex items-start gap-3 text-white/80" data-testid={`grp-cap-${n}`}>
                  <Check className="h-5 w-5 mt-0.5 flex-shrink-0 text-white" /> {t(`grp.cap.${n}`)}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative rounded-lg overflow-hidden border border-white/10 h-[420px]">
            <img src={IMAGES.transitBus} alt="Ford Sprinter and charter fleet for group transportation" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-24">
        <h2 className="font-serif font-light text-4xl text-white mb-12 text-center leading-tight">{t("grp.faq.title")}</h2>
        <Accordion type="single" collapsible className="w-full">
          {[1, 2, 3, 4].map((n) => (
            <AccordionItem key={n} value={`q${n}`} className="border-white/10">
              <AccordionTrigger className="text-white text-left hover:no-underline text-base">{t(`grp.faq.q${n}`)}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{t(`grp.faq.a${n}`)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="relative py-28">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.cityNight} alt="Montréal at night" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/75" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <h2 className="font-serif font-light text-4xl sm:text-5xl text-white leading-tight">{t("grp.cta.title")}</h2>
          <p className="text-white/70 mt-5 text-lg">{t("grp.cta.sub")}</p>
          <Button data-testid="grp-cta-btn" className="rounded-full bg-white text-black hover:bg-white/85 h-14 px-10 text-base mt-9"
            onClick={() => navigate("/book?service=transit_bus")}>
            {t("cat.quote")} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
